import { flow, Instance, types } from 'mobx-state-tree'
import { settlementFileDownloadReq } from '../../../src/network/settlementFileDownloadReq'
import { updateSettlementReportFormatReq } from '../../../src/network/updateSettlementReportFormat'
import { addSettlementReportFormatReq } from '../../network/addNewSettlementReport'
import { deleteSettlementReq } from '../../network/deleteSettlement'
import { deleteSettlementReportFormatReq } from '../../network/deleteSettlementReportFormat'
import { generateSettlement } from '../../network/generateSettlement'
import { generateSettlementReportsReq } from '../../network/generateSettlementReportsListBodyReq'
import { getSettlementSummary } from '../../network/getSettlementSummary'
import { deleteLoadingSettlement, settlementMobileParser } from '../../network/parsers/SettlementParser'
import { removeSettlement } from '../../network/removeSettlement'
import {
  FILE_DOWNLOAD_INPROCESS,
  FILE_DOWNLOADED,
  fromDateTime,
  getSelectedDate,
  isOsMobile,
  SESSION_EXPIRED,
  toDateTime,
} from '../../util/util'
import {
  settlementMobileTableParser,
  settlementRecentsTableParser,
  settlementTableParser,
} from '../parser/view/TableParser'
import { getGenerateSettlementFileBody } from '../request/requestbody'
import { mimeTypes, paginationSettingsWithDefaultPage1 } from '../static/data'
import { actionMenuStrings, customError } from '../static/strings'
import { downloadFile, downloadReportFileReq } from './../../network/downloadSettlementReportFormat'
import { extendAppSessionAPI } from './../../network/extendAppSession'
import { extendUserSessionAPI } from './../../network/extendUserSession'
import { generateSettlementFileReq } from './../../network/generateSettlementFileReq'
import { settlementCsvDownload } from './../../network/settlementCsvDownload'
import { settlementPdfDownload } from './../../network/settlementPdfDownload'
import { INFINITE_SCROLL_WINDOW_INDEX } from './../../util/common'
import { RootStore } from './../store'
import { Pagination } from './Transaction'

const Position = types.model('position', {
  left: types.number,
  top: types.number,
  visible: types.boolean,
})

const DateRange = types.model('date', {
  fromDate: types.Date,
  toDate: types.Date,
  position: Position,
  dateLabel: types.string,
})

const Attribute = types.model('Attribute', {
  id: types.optional(types.number, -1),
  field: types.optional(types.string, ''),
  sequence: types.optional(types.number, -1),
  alias: types.optional(types.string, ''),
  selected: types.optional(types.boolean, true),
})

const ReportAttributes = types.model('ReportAttributes', {
  id: types.optional(types.number, 0), //0 for forward transaction 1 for refunds
  attribute: types.array(Attribute),
})

export enum ReportFrequencyEnum {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  FORTNIGHTLY = 'FORTNIGHTLY',
}

export enum ReportFormatEnum {
  STANDARD = 'STANDARD',
  CUSTOM = 'CUSTOM',
  CUSTOM_RRL = 'CUSTOM_RRL',
}

export enum ReportTypeEnum {
  SETTLEMENT = 'SETTLEMENT',
}

const Report = types.model('SettlementReport', {
  id: types.optional(types.union(types.string, types.number), ''),
  merchantId: types.optional(types.string, ''),
  requestId: types.optional(types.string, ''),
  reportFormat: types.optional(types.enumeration(Object.values(ReportFormatEnum)), ReportFormatEnum.STANDARD),
  reportConfigTitle: types.optional(types.string, ''),
  reportFrequency: types.optional(types.enumeration(Object.values(ReportFrequencyEnum)), ReportFrequencyEnum.DAILY),
  reportValidity: types.optional(types.union(types.string, types.array(types.number)), ''),
  reportFileKey: types.optional(types.string, ''),
  reportExt: types.optional(types.string, ''),
  reportType: types.optional(types.string, ''),
  isSmid: types.optional(types.string, ''),
  createdDate: types.optional(types.string, ''),
  updatedDate: types.optional(types.string, ''),
  fileTransferMode: types.optional(types.string, ''),
  emailRecipients: types.optional(types.array(types.string), []),
  emailConfigPassKey: types.optional(types.string, ''),
  emailConfigSubjLine: types.optional(types.string, ''),
  emailConfigFileName: types.optional(types.string, ''),
  sftpNotifyRecipients: types.optional(types.array(types.string), []),
  sftpConfigPassKey: types.optional(types.string, ''),
  sftpConfigSubjLine: types.optional(types.string, ''),
  sftpConfigFileName: types.optional(types.string, ''),

  //reportEmail: types.optional(types.array(types.string), []),
  //emailFileTransferConfig: types.optional(FileTransferConfig, {}),
  // sftpFileTransferConfig: types.optional(FileTransferConfig, {}),
  // emailAndSftpFileTransferConfig: types.optional(EmailAndSftpFileTransferConfig, {}),

  // srno: types.optional(types.string, ''),
  // format: types.optional(types.string, ''),
  // date: types.optional(types.string, ''),
  // // name: types.optional(types.string, ''), //report name
  // title: types.optional(types.string, ''), //report title
  // // mode: types.optional(types.string, ''), //report transfer mode
  // // location: types.optional(types.string, ''), //report locationn url
  // frequency: types.optional(types.string, ''), //report frequency
  // validity: types.optional(types.string, ''), //report validity
  // // mailSubject: types.optional(types.string, ''), //report mail Subject
  // editDelete: types.optional(types.string, ''),
})

const Settlement = types.model('Settlement', {
  id: types.optional(types.string, ''),
  referenceNumber: types.optional(types.string, ''),
  settledAmount: types.optional(types.string, '0'),
  fromDate: types.optional(types.string, ''),
  toDate: types.optional(types.string, ''),
  date: types.optional(types.string, ''),
  settledTransactionCount: types.optional(types.number, 0),
  refundCount: types.optional(types.number, 0),
  refundAmount: types.optional(types.number, 0),
  settlementAmount: types.optional(types.string, '0'), // Net off Service fee, service tax, refunds and charge backs wherever applicable
  action: types.optional(types.string, 'Download'),
  fileName: types.optional(types.string, ''),
  loading: types.optional(types.string, ''),
  settlementDate: types.optional(types.string, ''),
})

const Settlements = types
  .model('Settlements', {
    settlement: types.array(Settlement),
    pagination: Pagination,
    selectedDateInfo: DateRange,
    reports: types.array(Report),
    attributes: types.array(ReportAttributes),
  })
  .actions((self) => ({
    fetchSettlementSummary: flow(function* fetchSettlementSummary(
      mid: string,
      startDateTime: string,
      endDateTime: string,
      pageNumber,
      size,
      _isLoadMore = false,
      isCustomFromRecent = false,
      abortController,
    ) {
      const own = self
      const getInputFilter = () => {
        return {
          merchantId: mid,
          startDateTime: startDateTime,
          endDateTime: endDateTime,
          isSMID: RootStore.user.isSmid ? 'Y' : 'N',
        }
      }
      const req = {
        inputFilter: getInputFilter(),
        configParams: {
          mediaType: 'JSON',
          page: pageNumber ?? 1,
          size: size, //limit of data
        },
      }
      let response
      try {
        response = yield getSettlementSummary(
          req,
          RootStore.user.session.getTokens,
          RootStore.user.session.userAccessToken,
          isCustomFromRecent,
          abortController,
        )
        const parsedData = response?.content
        own.settlement = _isLoadMore
          ? JSON.parse(JSON.stringify([...own.settlement, ...parsedData]))
          : JSON.parse(JSON.stringify(parsedData))
        // Fix for multiple calls on quickly paginating
        if (pageNumber === 1) {
          self.pagination = JSON.parse(JSON.stringify(response?.page))
        } else if (isOsMobile()) {
          self.pagination = JSON.parse(JSON.stringify(response?.page))
        }
      } catch (e) {
        if (!_isLoadMore) {
          own.settlement = JSON.parse(JSON.stringify([]))
          own.pagination = paginationSettingsWithDefaultPage1
        }
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),

    setSettlementDataFromStats: flow(function* setSettlementDataFromStats(response: any, _isLoadMore = false) {
      try {
        const own = self
        const parsedData = settlementMobileParser(response).content
        own.settlement = _isLoadMore
          ? JSON.parse(JSON.stringify([...own.settlement, ...parsedData]))
          : JSON.parse(JSON.stringify(parsedData))
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),

    settlementRemove: flow(function* settlementRemove(mid: string, reportId: string) {
      let response
      try {
        const req = {
          mid,
          reportId,
        }
        response = yield removeSettlement(req, RootStore.user.session.userAccessToken)
        const { data } = response
        console.log(data)
        self.settlement = JSON.parse(JSON.stringify(self.settlement.filter((newtrxn) => reportId !== newtrxn.id)))
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),

    settlementGenerate: flow(function* settlementGenerate(mid: string, reportDate: string) {
      let response
      const req = {
        mid,
        reportDate,
      }
      try {
        response = yield generateSettlement(
          req,
          RootStore.user.session.getTokens,
          RootStore.user.session.userAccessToken,
        )
        const { data } = response?.data
        console.log(data)
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),

    settlementPDFDownload: flow(function* settlementPDFDownload(mid: string, reportId: string, reportName: string) {
      const reqBody = {
        mid,
        reportId,
        reportName,
      }
      let resp
      try {
        resp = yield settlementPdfDownload(
          RootStore.user.session.getTokens,
          reqBody,
          RootStore.user.session.userAccessToken,
        )
        console.log(resp)
        const errorCode: any = resp?.error?.code
        if (window && errorCode === '' && resp && resp?.data) {
          //#TODO: Fix
          let contentType = resp.headers['content-type']
          if (mimeTypes.get(contentType) === undefined) {
            return
          } else {
            const blob = new Blob([resp?.data], { type: contentType })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            document.body.appendChild(a)
            a.href = url
            a.download = `${reportName}${mimeTypes.get(contentType)}`
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
          }
        }
      } catch (e) {
        console.log({ e })
      }
      return resp
    }),

    settlementCSVDownload: flow(function* settlementCSVDownload(mid: string, reportId: string, fileName: string) {
      console.log(reportId)
      const reqBody = {
        inputFilter: {
          merchantId: mid,
        },
      }
      let resp
      try {
        resp = yield settlementCsvDownload(RootStore.user.session.getTokens, reqBody, fileName)
        console.log(resp)
        if (window) {
          console.log(resp)
          console.log(resp.headers['content-type'])
          // let type = resp.headers['content-type']
          // const blob = new Blob([resp?.data], {
          //   type,
          // })
          // const url = window.URL.createObjectURL(blob)
          // console.log('url: ', url)
          // const a = document.createElement('a')
          // document.body.appendChild(a)
          // a.href = url
          // a.download = `${fileName || `file${mimeTypes.get(type)}`}`
          // a.click()
          // window.URL.revokeObjectURL(url)
          // document.body.removeChild(a)
        }
      } catch (e) {
        console.log({ e })
      }
      return resp
    }),
    settlementFileDownload: flow(function* (
      mid: string,
      reportId: string,
      fileName: string,
      setlmentFileDwnld: any,
      fileType: string,
      fromDate: string,
      toDate: string,
    ) {
      const downloadConfigMap = {
        Custom: {
          mediaType: 'XLSX',
          sortType: 'DESC',
          customReport: 'Y',
        },
        'Custom-CSV': {
          mediaType: 'CSV',
          sortType: 'DESC',
          customReport: 'Y',
        },
        'Custom-TXT': {
          mediaType: 'TXT',
          sortType: 'DESC',
          customReport: 'Y',
        },
      }
      const getConfigParams = (file: string) => {
        return (
          downloadConfigMap[file as keyof typeof downloadConfigMap] ?? {
            mediaType: file,
            sortType: 'DESC',
          }
        )
      }

      let isFileDownloading = true
      const reqBody = {
        inputFilter: {
          merchantId: mid,
          startDateTime: fromDate,
          endDateTime: toDate,
          isSMID: RootStore.user.isSmid ? 'Y' : 'N',
        },
        configParams: getConfigParams(fileType),
        outputDomains: [
          'transactionDateTime',
          'invoice',
          'transactionId',
          'transactionAmount',
          'transactionDisposition',
          'cardType',
          'paymentType',
          'issRefData',
        ],
      }
      let resp
      try {
        resp = yield settlementFileDownloadReq(RootStore.user.session.getTokens, reqBody, fileName ?? mid)
        if (isOsMobile()) {
          if (resp?.code === FILE_DOWNLOADED) {
            return resp
          } else if (resp?.code === FILE_DOWNLOAD_INPROCESS) {
            RootStore.reports.setMessage(
              "We're working on the report you requested. You can access it here after it is generated",
            )
          } else if (resp?.code === SESSION_EXPIRED) {
            yield extendAppSessionAPI()
            yield extendUserSessionAPI()
            return yield (self as any).setlmentFileDwnld(mid, reportId, fileName, setlmentFileDwnld, fileType)
          } else {
            RootStore?.response?.setMessage('Error while exporting')
          }
          isFileDownloading = false
          return resp
        } else {
          const contentType = resp.headers.get('content-type')
          if (contentType === 'application/json' || !contentType) {
            resp = yield resp.json()
            if (resp?.status === 'SUCCESS' && resp?.data?.status === 'INPROCESS') {
              RootStore.reports.setMessage("Your report will be available in the 'Downloads' section in some time.")
            } else if (resp?.status === 'SUCCESS' && resp?.data?.status === 'FAILED') {
              RootStore?.response?.setMessage('Export file failed')
            } else if (resp.status === 'FAILURE' && resp.error.code === '33306') {
              yield extendAppSessionAPI()
              yield extendUserSessionAPI()
              return (self as any).settlementFileDownload(
                mid,
                reportId,
                fileName,
                setlmentFileDwnld,
                fileType,
                fromDate,
                toDate,
              )
            } else if (resp.status === 'FAILURE' && resp.error.code === '33607') {
              yield extendUserSessionAPI()
              return (self as any).settlementFileDownload(
                mid,
                reportId,
                fileName,
                setlmentFileDwnld,
                fileType,
                fromDate,
                toDate,
              )
            } else {
              RootStore?.response?.setMessage(resp.error?.message ?? customError.errorOccured)
            }
          } else {
            downloadFile(resp, fileName)
          }
        }
      } catch (error) {
        isFileDownloading = true
        RootStore?.response?.setMessage(error?.data?.error?.message ?? customError.errorOccured)
        throw error
      }
      return isFileDownloading
    }),
    generateSettlementFile: flow(function* generateSettlementFile(
      fileType: string,
      isMobile?: false,
      settlementDate?: any,
    ) {
      const pageNumber = self.pagination.number === 0 ? 1 : self.pagination.number
      const sizeLimit = self.pagination.size < 10 ? 10 : self.pagination.size
      const mid = RootStore.user.selectedMid
      const endDateTime =
        toDateTime(self?.selectedDateInfo?.toDate ?? new Date(Date.now())) ??
        toDateTime(RootStore.settings.metaData.selectedDate.toDate ?? new Date(Date.now()))
      const startDateTime =
        fromDateTime(self?.selectedDateInfo?.fromDate) ??
        fromDateTime(RootStore.settings.metaData.selectedDate.fromDate)

      let enDate = +endDateTime > Date.now() ? Date.now() : endDateTime // 5 min before txn
      let own = self
      //Commented as showing Loader instead of showing loading first row in table
      // own.settlement = JSON.parse(JSON.stringify([getLoadingData(), ...self.settlement]))

      const reqBody = getGenerateSettlementFileBody(
        mid,
        startDateTime,
        `${enDate}`,
        fileType,
        RootStore.user.isSmid,
        isMobile,
        settlementDate,
      )
      let resp
      try {
        resp = yield generateSettlementFileReq(
          RootStore.user.session.getTokens,
          reqBody,
          RootStore.user.session.userAccessToken,
        )
        if (resp?.data?.error) {
          throw resp.data
        }
      } catch (e) {
        const message = e?.data?.error?.message ?? customError.errorOccured
        !isMobile && RootStore?.response?.setMessage(message)
        //Commented as showing Loader instead of showing loading first row in table
        // own.settlement = JSON.parse(JSON.stringify(deleteLoadingSettlement(own.settlement)))
        throw new Error(message)
      }

      own.settlement = JSON.parse(JSON.stringify(deleteLoadingSettlement(own.settlement)))
      // sync settlement data
      if (!isMobile) {
        const req = {
          inputFilter: {
            merchantId: mid,
            startDateTime: startDateTime, //dateFormatter(startDateTime, DateFormatTypes.YYYYMMDD),
            endDateTime: `${enDate}`, //endData,
            isSMID: RootStore.user.isSmid ? 'Y' : 'N',
          },
          configParams: {
            mediaType: 'JSON',
            page: pageNumber ?? 1,
            size: sizeLimit, //limit of data
          },
        }
        let response
        try {
          response = yield getSettlementSummary(
            req,
            RootStore.user.session.getTokens,
            RootStore.user.session.userAccessToken,
          )
          const parsedData = response?.content
          own.settlement = JSON.parse(JSON.stringify(parsedData))
          own.pagination = JSON.parse(JSON.stringify(response?.page))
        } catch (e) {
          own.settlement = JSON.parse(JSON.stringify(deleteLoadingSettlement(own.settlement)))
          RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
          throw new Error(e?.data?.error?.message ?? 'Error while processing request')
        }
        return response
      }
    }),
    updateSelectedDateInfo(dateObj: any): void {
      self.selectedDateInfo.fromDate = dateObj?.fromDate ?? new Date(Date.now())
      self.selectedDateInfo.toDate = dateObj?.toDate ?? new Date(Date.now())
      self.selectedDateInfo.position.top = dateObj?.position?.top ?? 0
      self.selectedDateInfo.position.left = dateObj?.position?.left ?? 0
      self.selectedDateInfo.position.visible = dateObj?.position?.visible ?? false
      self.selectedDateInfo.dateLabel = dateObj?.dateLabel ?? ''
    },
    updatePagination(selectedPage: number): void {
      self.pagination.number = selectedPage
    },
    resetPagination(): void {
      self.pagination = JSON.parse(JSON.stringify(paginationSettingsWithDefaultPage1))
    },
    fetchSettlementReportsFormatListApiList: flow(function* fetchSettlementReportsFormatListApiList(
      mid: any,
      pageNum: any,
    ) {
      let resp
      const own = self
      try {
        //console.log('called api')
        resp = yield generateSettlementReportsReq(
          RootStore.user.session.getTokens,
          mid,
          pageNum,
          RootStore.user.session.userAccessToken,
          RootStore.user.isSmid,
        )
        const result = JSON.parse(JSON.stringify(resp))
        let response = result?.content
        response?.forEach((element: any, index: number = 1) => {
          element.tableIndex = result?.page?.number > 1 ? 10 * result?.page?.number - index + 1 - 10 : index + 1
        })
        RootStore.reports.setPagination(result?.page)
        own.reports = response
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return resp
    }),
    deleteSettlement: flow(function* deleteSettlementReportFormat(data: any, mid: string) {
      let response
      try {
        const reqdata = {
          inputFilter: {
            startDateTime: data?.fromDateOrg,
            endDateTime: data?.toDateOrg,
            merchantId: mid,
            isSMID: RootStore.user.isSmid ? 'Y' : 'N',
            transactionType: ReportTypeEnum.SETTLEMENT,
          },
        }
        response = yield deleteSettlementReq(reqdata, RootStore.user.session.getTokens)
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    deleteSettlementReportFormat: flow(function* deleteSettlementReportFormat(id: string, mid: string) {
      let response
      try {
        response = yield deleteSettlementReportFormatReq(id, mid, RootStore.user.session.getTokens)
        console.log('deleteSettlementReportFormat response', response)
        if (response?.status === 'SUCCESS') {
          //handle success delete
          RootStore?.response?.setBgColor('GREEN')
          RootStore?.response?.setMessage('Report format deleted successfully')
        }
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    updateSettlementReportFormat: flow(function* updateSettlementReportFormat(
      id: string,
      mid: string,
      updateObject: any,
    ) {
      let response
      try {
        response = yield updateSettlementReportFormatReq(
          id,
          mid,
          updateObject,
          RootStore.user.session.getTokens,
          RootStore.user.isSmid,
        )
        //console.log('updateSettlementReportFormat response', response)
        if (response?.status === 'SUCCESS') {
          RootStore?.response?.setBgColor('GREEN')
          RootStore?.response?.setMessage(
            response?.data?.message ? response?.data?.message : 'Updated Report Format Successfully',
          )
        } else {
          RootStore?.response?.setBgColor('RED')
          RootStore?.response?.setMessage(
            response?.data?.message ? response?.data?.message : 'Something went wrong. Please try again later.',
          )
        }
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
      }
    }),
    downloadSampleReportFormat: flow(function* downloadReport(reportingFormat: string, reportingType: string) {
      let resp
      try {
        let reportingTypeCopy: string = reportingType
        let reportName: string = 'STANDARD'
        let fileName =
          reportingType === 'XLSX'
            ? 'SETT_mid_yyyyddmmhhmmss'
            : reportingType === 'CSV'
            ? 'SETT_mid_yyyyddmmhhmmss'
            : 'SETT_mid_yyyyddmmhhmmss'

        if (reportingFormat === 'Custom') {
          fileName =
            reportingType === 'XLSX'
              ? 'SETT_mid_yyyyddmmhhmmss_custom'
              : reportingType === 'CSV'
              ? 'SETT_mid_yyyyddmmhhmmss_custom'
              : 'SETT_mid_yyyyddmmhhmmss_custom'
          reportName = 'CUSTOM'
        } else if (reportingFormat === 'Custom RRL') {
          fileName =
            reportingType === 'XLSX'
              ? 'SETT_mid_yyyyddmmhhmmss_custom_rrl_pos'
              : reportingType === 'CSV'
              ? 'SETT_mid_yyyyddmmhhmmss_custom_rrl_pos'
              : 'SETT_mid_yyyyddmmhhmmss_custom_rrl_pos'
          reportName = 'CUSTOM_RRL_POS'
          // reportingTypeCopy = reportingType === 'TXT' ? 'invalid request' : ''
        }

        const payload = () => {
          let payload: any = {
            fileName: fileName,
            reportName: reportName,
          }
          if (reportingFormat.toLowerCase().includes('custom rrl')) {
            payload.configParams = { mediaType: reportingTypeCopy }
          } else {
            payload.configParams = {
              mediaType: reportingTypeCopy,
            }
          }
          return payload
        }

        resp = yield downloadReportFileReq(payload(), RootStore.user.session.getTokens, 'POST')
        downloadFile(resp, fileName)
      } catch (e) {
        console.log({ e })
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
      }
    }),
    updateSelectedAttributesItem(id: number, item: any) {
      const own = self
      own.attributes[id].attribute.filter((attr) => attr.id === item.id)[0].selected = item.selected
      return JSON.parse(JSON.stringify(own.attributes[id].attribute))
    },
    updateAttributesItemAlias(id: number, item: any) {
      const own = self
      console.log('===> updateAttributesItemAlias', item.alias)
      own.attributes[id].attribute.filter((attr) => attr.id === item.id)[0].alias = item.alias
      return JSON.parse(JSON.stringify(own.attributes[id].attribute))
    },
    updateAttributesItemSequence(id: number, item: any) {
      const own = self
      const existing = own.attributes[id].attribute.filter((attr) => attr.id === item.id)[0]
      console.log('===> existing item sequence', existing.sequence, +item.sequence)
      own.attributes[id].attribute.filter((attr) => attr.sequence === +item.sequence)[0].sequence = +existing.sequence
      own.attributes[id].attribute.filter((attr) => attr.id === item.id)[0].sequence = +item.sequence
      return JSON.parse(JSON.stringify(own.attributes[id].attribute))
    },
    addNewSettlementReportApi: flow(function* addNewSettlementReportApi(mid: string, object: any) {
      let response
      try {
        response = yield addSettlementReportFormatReq(
          mid,
          object,
          RootStore.user.session.getTokens,
          RootStore.user.isSmid,
        )
        //console.log('addUpdateSettlementReportApi response', response)
        if (response?.status === 'SUCCESS') {
          RootStore?.response?.setBgColor('GREEN')
          RootStore?.response?.setMessage(
            response?.data?.message ? response?.data?.message : 'Report Format Added Successfully',
          )
          self.reports.push(object)
          //handle success delete
        } else {
          RootStore?.response?.setBgColor('RED')
          RootStore?.response?.setMessage(
            response?.data?.message ? response?.data?.message : 'Something went wrong. Please try again later.',
          )
        }
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
      }
      return response
    }),
    getSettlementReportById(id: string) {
      const result = self.reports?.find((item: any) => item.id === id)
      return result
    },
    resetSettlementFilterDate() {
      self.selectedDateInfo.fromDate = isOsMobile() ? getSelectedDate(INFINITE_SCROLL_WINDOW_INDEX) : getSelectedDate(1)
      self.selectedDateInfo.toDate = new Date(Date.now())
    },
  }))
  .views((self) => ({
    fetchSettlementData(isMobile?: boolean): any[] {
      const canExportSettlement = RootStore.userManagement.isSettlementExportEnabled()
      const trxn = isMobile
        ? settlementMobileTableParser(self.settlement)
        : settlementTableParser(self.settlement, canExportSettlement)
      if (trxn.length === 0) {
        return []
      }
      if (isMobile) {
        return trxn.slice(0)
      }
      return isMobile ? trxn.slice(0, 10) : [...trxn.slice(0, 10)]
    },
    fetchSettlementDataRecents(isMobile: boolean): any[] {
      const trxn = settlementRecentsTableParser(self.settlement)
      if (trxn.length === 0) {
        return []
      }
      if (isMobile) {
        return trxn.slice(0, 10)
      }
      return [...trxn.slice(0, 10)]
    },
    getSettlementReportsData() {
      return [...self.reports.slice(0, 10)]
    },
    getReportAttributes(id: number): any {
      return self.attributes[id]
    },
    floatSelectionsToTop(id: number): any {
      const list = self.attributes[id].attribute
      return [...list.filter((item) => item.selected), ...list.filter((item) => !item.selected)]
    },
    getAllReportAttributes(): any {
      return JSON.parse(JSON.stringify(self.attributes))
    },
    searchSettlementById(id: string): any {
      return self.settlement.filter((data: any) => data.id === id)[0]
    },
    getSettlementDetails(id: string): any {
      return self?.settlement?.find((data: any) => data.referenceNumber === id)
    },
  }))

const SettlementInitialData = Settlements.create({
  settlement: [],
  pagination: paginationSettingsWithDefaultPage1,
  selectedDateInfo: {
    fromDate: isOsMobile() ? getSelectedDate(INFINITE_SCROLL_WINDOW_INDEX) : getSelectedDate(1),
    toDate: new Date(Date.now()),
    position: {
      top: 0,
      left: 0,
      visible: false,
    },
    dateLabel: isOsMobile() ? actionMenuStrings.infiniteScrollRange : 'Today',
  },
  reports: [],
  attributes: [
    {
      id: 0,
      attribute: [
        { id: 0, field: 'Payment Id', sequence: 1, aliasName: '', selected: true },
        { id: 1, field: 'Order Id', sequence: 2, aliasName: '', selected: true },
        { id: 2, field: 'Amount', sequence: 3, aliasName: '', selected: true },
        { id: 3, field: 'Status', sequence: 4, aliasName: '', selected: true },
        { id: 4, field: 'Payment method', sequence: 5, aliasName: '', selected: true },
        { id: 5, field: 'Transaction date', sequence: 6, aliasName: '', selected: true },
        { id: 6, field: 'Settlement date', sequence: 7, aliasName: '', selected: true },
        { id: 7, field: 'Description', sequence: 8, aliasName: '', selected: true },
        { id: 8, field: 'Customer e-mail', sequence: 9, aliasName: '', selected: true },
        { id: 9, field: 'Customer Phone', sequence: 10, aliasName: '', selected: true },
        { id: 10, field: 'TXN Fees', sequence: 11, aliasName: '', selected: true },
        { id: 11, field: 'Notes', sequence: 12, aliasName: '', selected: true },
      ],
    },
    {
      id: 1,
      attribute: [
        { id: 0, field: 'Payment Id', sequence: 1, aliasName: '', selected: true },
        { id: 1, field: 'Order Id', sequence: 2, aliasName: '', selected: true },
        { id: 2, field: 'Amount', sequence: 3, aliasName: '', selected: true },
        { id: 3, field: 'Status', sequence: 4, aliasName: '', selected: true },
        { id: 4, field: 'Payment method', sequence: 5, aliasName: '', selected: true },
        { id: 5, field: 'Transaction date', sequence: 6, aliasName: '', selected: true },
        { id: 6, field: 'Settlement date', sequence: 7, aliasName: '', selected: true },
        { id: 7, field: 'Description', sequence: 8, aliasName: '', selected: true },
        { id: 8, field: 'Customer e-mail', sequence: 9, aliasName: '', selected: true },
        { id: 9, field: 'Customer Phone', sequence: 10, aliasName: '', selected: true },
        { id: 10, field: 'TXN Fees', sequence: 11, aliasName: '', selected: true },
        { id: 11, field: 'Notes', sequence: 12, aliasName: '', selected: true },
      ],
    },
  ],
})

export type settlementType = Instance<typeof Settlements>
export { Settlement, SettlementInitialData, Settlements }
